/**
 * Tells an open tab that a newer build is deployed.
 *
 * The Angular service worker only looks for a new build when the page loads,
 * so a tab left open runs the build it opened with. This asks the worker for
 * a new build every {@link UPDATE_CHECK_INTERVAL_MS} and whenever the tab
 * regains focus or becomes visible (at most every {@link MIN_CHECK_GAP_MS}).
 * Once the worker has downloaded the new build, `updateAvailable` turns true
 * and the app shell shows a banner with a Reload button.
 *
 * It does nothing where the worker is off (`ng serve`, unsupported browsers).
 */
import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { PAGE_LOCATION } from './chunk-reload';

/** How often an open tab asks for a newer build. */
export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Focus and visibility checks run at most this often. */
export const MIN_CHECK_GAP_MS = 30 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly page = inject(PAGE_LOCATION);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _updateAvailable = signal(false);
  readonly updateAvailable = this._updateAvailable.asReadonly();

  private started = false;
  private lastCheckAt = 0;

  start(): void {
    if (this.started || !this.swUpdate?.isEnabled) return;
    this.started = true;

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') this._updateAvailable.set(true);
    });

    // Outside the zone, so the poll never keeps the app from going stable
    // (the worker registers on stability).
    this.zone.runOutsideAngular(() => {
      const poll = setInterval(() => this.check(), UPDATE_CHECK_INTERVAL_MS);
      const onReturn = () => {
        if (this.document.visibilityState === 'hidden') return;
        if (Date.now() - this.lastCheckAt >= MIN_CHECK_GAP_MS) this.check();
      };
      this.document.addEventListener('visibilitychange', onReturn);
      this.document.defaultView?.addEventListener('focus', onReturn);
      this.destroyRef.onDestroy(() => {
        clearInterval(poll);
        this.document.removeEventListener('visibilitychange', onReturn);
        this.document.defaultView?.removeEventListener('focus', onReturn);
      });
    });
  }

  /** Loads the new build. The worker already holds it, so the reload is atomic. */
  reload(): void {
    this.page.reload();
  }

  private check(): void {
    this.lastCheckAt = Date.now();
    // A failed check (offline, a deploy mid-rollout) is retried by the next one.
    this.swUpdate?.checkForUpdate().catch(() => undefined);
  }
}
